import React, { useState, useEffect, useCallback } from 'react';
import { getSuggestions } from '../../services/getSuggestions';
import { MAX_RETRIES } from '../../constantes/constantes';

/**
 * Componente Sugerencias
 * Muestra un listado de sugerencias predictivas basado en una palabra clave.
 * Permite navegar por teclado en desktop y con touch/mouse en mobile.
*/
export default function Sugerencias({
  palabra,
  setText,
  setTextNew,
  setShowSugerencias,
  inputRef,
  token,
  hasFocus,
  setQuery,
  setErrorService,
  category,
  isMobile,
  onQueryChange,
  listaSugerencias,
  setListaSugerencias,
  setSelectedIndex,
  selectedIndex
}) {
  const [retryCount, setRetryCount] = useState(0);

  /**
   * Obtiene sugerencias desde el backend cuando hay enfoque y una palabra válida.
  */
  const fetchSuggestions = useCallback(() => {
    if (palabra && palabra.length >= 3 && hasFocus && retryCount < 1) {
      getSuggestions(palabra, token, category)
        .then((sugerencia) => {
          if (sugerencia?.statusCode === 200) {
            setListaSugerencias(sugerencia.data);
          } else if (sugerencia?.statusCode === 401) {
            handleUnauthorizedError();
          } else {
            setListaSugerencias([]);
          }
        })
        .catch(() => {
          setListaSugerencias([]);
        });
    } else {
      setListaSugerencias([]);
    }
  }, [palabra, hasFocus, token, category]);

  /**
   * Maneja error 401 con reintentos controlados.
  */
  const handleUnauthorizedError = useCallback(() => {
    if (retryCount < 1) {
      setRetryCount(retryCount + 1);
      setErrorService(true);
      // callPostToken();
    } else {
      console.error(`Se alcanzó el máximo de reintentos: ${MAX_RETRIES}`);
    }
  }, [retryCount]);

  /**
   * Navegación por el listado de sugerencias a traves del teclado para desktop.
  */
  const handleKeyDown = useCallback((e) => {
    const key = e.key || e.which || e.keyCode;

    if (key === 'ArrowDown') {
      setSelectedIndex((prev) => Math.min(prev + 1, listaSugerencias.length - 1));
    } else if (key === 'ArrowUp') {
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (key === 'Enter' || key === 'Go' || key === 13) {
      const selected = listaSugerencias[selectedIndex]?.sugerencia;
      if (selected) {
        const nuevoValor = cleanSuggestionText(selected);
        if (inputRef.current) {
          inputRef.current.value = nuevoValor;
        }
        setTextNew(nuevoValor);
        setSelectedIndex(-1);
        setTimeout(() => setShowSugerencias(false), 100);
      }
    }
  }, [selectedIndex, listaSugerencias, inputRef]);

  /**
   * Selecciona una sugerencia de la lista.
  */
  const selectSuggestion = (valor) => {
    const nuevoValor = cleanSuggestionText(valor);
    setText(nuevoValor);
    setQuery(nuevoValor);
    setTextNew(nuevoValor);
    onQueryChange(nuevoValor);
    setTimeout(() => { setShowSugerencias(false); }, 100);
  }

  /**
   * Elimina la última palabra de la sugerencia y limpia caracteres no deseados.
   */
  const cleanSuggestionText = (text) => {
    let palabras = text.split(' ');
    if (palabras.length > 1) palabras = palabras.slice(0, -1);
    return palabras.join(' ').trim().replace(/^[^a-zA-Z0-9]+/, '');
  };

  /**
   * Posiciona el cursor al final del texto en el input.
  */
  const setCaretPositionToEnd = (input) => {
    const length = palabra.length;
    input.focus();
    input.setSelectionRange(length, length);
  };

  //--------  Efectos -------- 
  // Efecto para hacer la petición de sugerencias
  useEffect(() => { fetchSuggestions(); }, [fetchSuggestions]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (selectedIndex >= 0 && selectedIndex < listaSugerencias.length && inputRef.current) {
      setCaretPositionToEnd(inputRef.current);
    }
  }, [selectedIndex, listaSugerencias, inputRef]);

  return (
    hasFocus && listaSugerencias?.length > 0 && (
      <div className='lista-sugerencias' role="listbox" aria-label="Sugerencias de búsqueda">
        <ul>
          {listaSugerencias.filter((_, i) => !isMobile || i < 3).map((ele, i) => (
            <li 
              key={i}
              id={`suggestion-${i}`}
              role="option"
              className={i === selectedIndex ? 'selected' : ''}
              onTouchEnd={() => selectSuggestion(ele.sugerencia)}
              onMouseDown={() => selectSuggestion(ele.sugerencia)}
            >
              <span className='icon-search' aria-hidden='true'></span> {cleanSuggestionText(ele.sugerencia)}
            </li>
          ))}
        </ul>
      </div>
    )
  )
}